/// <amd-module name="Pipeline/Analytics/DurationCardMetrics/PipelineDuration.CardMetrics" />
import { AnalyticsExceptionType, recognizeAnalyticsException } from "Analytics/Common/Utilities/AnalyticsExceptionUtilities";
import { ContextType, IReportContext, ITrendData, Period, TrendBy, TrendType } from "Analytics/Shared/Common/Types";
import { getTrendFromData } from "Analytics/Shared/Common/Utils/TrendCalculator";
import { ITrendChartData, ITrendChartReportConfiguration } from "Analytics/Shared/TrendChart/Common/Types";
import { getPercentage } from "Analytics/Shared/Utils/Number";
import { AreaChartHelper } from "Analytics/Shared/Visuals/AreaChartView/Common/AreaChartHelper";
import { AreaChartView } from "Analytics/Shared/Visuals/AreaChartView/ControllerViews/AreaChartView";
import { CardMetricsVisual } from "Analytics/Shared/Visuals/CardMetricsVisual/CardMetricsVisual";
import { percentile_text } from "Pipeline/Analytics/Common/Constants";
import { IPipelineAnalyticsTelemetryService } from "Pipeline/Analytics/Common/Telemetry/PipelineAnalyticsTelemetryService";
import * as TelemetryConstants from "Pipeline/Analytics/Common/Telemetry/TelemetryConstants";
import * as Resources from "Pipeline/Analytics/DurationCardMetrics/Resources";
import { DurationCardMetricsSource } from "Pipeline/Analytics/DurationCardMetrics/Sources/DurationCardMetricsSource";
import { PipelineDurationChartSeries } from "Pipeline/Analytics/PipelineDurationCommon/ChartSeries/PipelineDurationChartSeries";
import { PipelineDurationInsights } from "Pipeline/Analytics/PipelineDurationCommon/PipelineDurationInsights";
import { IMaxDurationTaskData, IPipelineDurationData } from "Pipeline/Analytics/PipelineDurationCommon/Types";
import { SuccessfulPipelines } from "Pipeline/Analytics/Common/Constants";
import * as React from "react";
import { localeFormat } from "VSS/Core/Util/String";
import { ChartProps } from "VSS/Features/Charts/Contracts";
import { Components, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Duration } from "VSSUI/Components/Time/Duration/Duration";

export interface ICardMetricsProps extends IVssComponentProperties {
    definitionId: number;
    onCardClick: (routeId: string, event?: any) => void;
    contextType: string;
    errorText?: string;
}

export interface IDurationCardMetricsState {
    duration: string | undefined;
    shortDuration: number | undefined;
    maxDurationTaskDisplayName: string;
    maxDurationTaskPercentage: number | undefined;
    totalRuns: number | undefined;
    definitionId: number;
    chartData?: ITrendChartData;
    trend?: ITrendData;
    errorText?: string;
    info?: string;
    iconName?: string;
}

export class DurationCardMetrics extends VssComponent<ICardMetricsProps, IDurationCardMetricsState> {
    private static readonly _pipelineDurationHubRouteUrl: string = "ms.vss-pipelineanalytics-web.pipeline-duration-route";
    private _pipelineDurationDataPromise: Promise<IPipelineDurationData>;
    private _maxDurationTaskDataPromise: Promise<IMaxDurationTaskData>;
    private _trendChartDataPromise: Promise<ITrendChartData>;
    private _pipelineDurationInsights: PipelineDurationInsights;
    private _isTrendGood: boolean;

    constructor(props: ICardMetricsProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            errorText: this.props.errorText,
            definitionId: this.props.definitionId
        } as Readonly<IDurationCardMetricsState>;
        this._pipelineDurationInsights = new PipelineDurationInsights();
    }

    public async componentDidMount(): Promise<void> {
        super.componentDidMount();
        if (!this.props.errorText) {
            await this._refreshData();
        }
    }

    public render() {
        return (
            <CardMetricsVisual
                title={Resources.PipelineDuration}
                dataValidationPromises={[this._pipelineDurationDataPromise, this._maxDurationTaskDataPromise]}
                primaryMetric={
                    this.state.shortDuration != null
                        ? {
                              value: this._getDurationElement(this.state.shortDuration)
                          }
                        : undefined
                }
                secondaryMetric={
                    this.state.totalRuns
                        ? {
                              label: Resources.SuccessfulRuns,
                              value: this.state.totalRuns.toString()
                          }
                        : undefined
                }
                trend={this.state.trend}
                isTrendGood={this._isTrendGood}
                insight={this._pipelineDurationInsights.getPipelineDurationInsights(
                    this.state.maxDurationTaskPercentage,
                    this.state.maxDurationTaskDisplayName
                )}
                subtitle={localeFormat(Resources.PipelineDurationSubTitle, percentile_text)}
                onCardClick={this._onCardClick}
                errorText={this.state.errorText}
                info={this.state.info}
                iconName={this.state.iconName}
            >
                {this._verifyChartData(this.state.chartData) && (
                    <AreaChartView chartOptions={this._getChartOptions(this.state.chartData!)} />
                )}
            </CardMetricsVisual>
        );
    }

    public componentDidUpdate(prevProps: ICardMetricsProps, prevState: IDurationCardMetricsState): void {
        if (this.props.definitionId !== this.state.definitionId) {
            this.setState(
                {
                    definitionId: this.props.definitionId,
                    duration: undefined,
                    shortDuration: undefined,
                    maxDurationTaskPercentage: undefined,
                    totalRuns: undefined,
                    trend: undefined,
                    chartData: undefined,
                    errorText: undefined,
                    info: undefined
                },
                () => {
                    this._refreshData();
                }
            );
        }

        super.componentDidUpdate(prevProps, prevState);
    }

    private _getChartOptions = (chartData: ITrendChartData): ChartProps => {
        const _areaChartHelper: AreaChartHelper = new AreaChartHelper();
        const data = chartData.primaryChartData[SuccessfulPipelines];
        const chartSeries = new PipelineDurationChartSeries(data.map(d => d.aggrValue));
        let chartProps = _areaChartHelper.getAreaChartOptions(this.context, data, chartSeries, true);
        chartProps = _areaChartHelper.setGradientFill(chartProps, chartSeries.color);
        return chartProps;
    };

    private _verifyChartData = (chartData: ITrendChartData | undefined): boolean => {
        return chartData && chartData.primaryChartData && chartData.primaryChartData[SuccessfulPipelines]?.length > 1 ? true : false;
    };
    
    private _onCardClick = (event?: React.MouseEvent<HTMLDivElement>): void => {
        if (this.props.onCardClick) {
            this.props.onCardClick(DurationCardMetrics._pipelineDurationHubRouteUrl, event);
            this._publishPipelineCardClickTelemetry();
        }
    };

    private _publishPipelineCardClickTelemetry(): void {
        const properties: {
            [key: string]: any;
        } = {};
        properties[TelemetryConstants.PercentileDuration] = this.state.shortDuration;
        properties[TelemetryConstants.TaskDurationPercentage] = this.state.maxDurationTaskPercentage;
        const telemetryService = this.context.pageContext.getService<IPipelineAnalyticsTelemetryService>("IPipelineAnalyticsTelemetryService");
        telemetryService.publishEvent(TelemetryConstants.feature_PipelineAnalyticsDurationCard, properties);
    }

    private _publishPipelineNotClickCardTelemetry(): void {
        if (!this.state.errorText) {
            const properties: {
                [key: string]: any;
            } = {};
            properties[TelemetryConstants.PercentileDuration] = this.state.shortDuration;
            properties[TelemetryConstants.TaskDurationPercentage] = this.state.maxDurationTaskPercentage;
            const telemetryService = this.context.pageContext.getService<IPipelineAnalyticsTelemetryService>("IPipelineAnalyticsTelemetryService");
            telemetryService.publishEvent(TelemetryConstants.feature_Analytics_PipelineDuration_CardLoaded, properties);
        }
    }

    private async _refreshData(): Promise<void> {
        const reportContext = {
            contextType: ContextType.Build,
            definitionId: this.state.definitionId
        } as IReportContext;

        const defaultConfValues = {
            trendBy: TrendBy.Days,
            period: Period.Days_14
        } as ITrendChartReportConfiguration;

        const durationCardMetricsSource = new DurationCardMetricsSource(this.context.pageContext);
        this._pipelineDurationDataPromise = durationCardMetricsSource.queryPipelineDurationData(reportContext, defaultConfValues);
        this._maxDurationTaskDataPromise = durationCardMetricsSource.getMaxDurationTaskData(reportContext, defaultConfValues);

        this._pipelineDurationDataPromise
            ?.then(pipelineDurationData => {
                if (pipelineDurationData.percentileDuration && pipelineDurationData.pipelineCount > 0) {
                    this.setState({
                        shortDuration: pipelineDurationData.percentileDuration * 1000,
                        totalRuns: pipelineDurationData.pipelineCount,
                        errorText: undefined,
                        info: undefined,
                        iconName: undefined
                    });
                } else {
                    this.setState({ info: Resources.PipelinesNotAvailable, iconName: "Rocket" });
                }
            })
            .catch(error => {
                this._onError(error);
            });

        this._maxDurationTaskDataPromise?.then(maxDurationTaskData => {
            const maxDurationTaskPercentage = getPercentage(maxDurationTaskData.allTasksDuration, maxDurationTaskData.taskDuration);
            this.setState({
                maxDurationTaskPercentage: maxDurationTaskPercentage,
                maxDurationTaskDisplayName: maxDurationTaskData.taskName
            });
        });

        this._trendChartDataPromise = durationCardMetricsSource.queryPipelineDurationTrendData(reportContext, defaultConfValues);
        this._trendChartDataPromise.then(trendChartData => {
            let trend = undefined;
            if (this._verifyChartData(trendChartData)) {
                const len = trendChartData.primaryChartData[SuccessfulPipelines].length;
                const startValue = trendChartData.primaryChartData[SuccessfulPipelines][0].aggrValue;
                const endValue = trendChartData.primaryChartData[SuccessfulPipelines][len - 1].aggrValue;
                trend = getTrendFromData(startValue, endValue, true);
                this._isTrendGood = trend && trend.trendType == TrendType.Decreased ? true : false;
            }
            this.setState({ chartData: trendChartData, trend: trend });
        });

        Promise.all([this._pipelineDurationDataPromise, this._maxDurationTaskDataPromise]).then(() => {
            this._publishPipelineNotClickCardTelemetry();
        });
    }

    private _onError(error: any) {
        let errorText: string = error.toString();
        if (recognizeAnalyticsException(error) === AnalyticsExceptionType.DataNotReady) {
            errorText = Resources.DataNotReady;
        }
        this.setState({ errorText: errorText, iconName: "ErrorBadge" });
    }

    private _getDurationElement = (value: number): JSX.Element => {
        return <Duration startDate={new Date(0)} endDate={new Date(value)} tooltipProps={null} ariaLabel={" "} />;
    };
}

Components.add("durationCardMetrics", DurationCardMetrics);
